/**
 * Manager Messages. Two tabs:
 *  - Clients: message threads with clients (reuses /firm/client-messages/*).
 *             If a client is active (header picker), only that client's thread;
 *             otherwise a conversation list to pick from.
 *  - Staff:   the firm's internal staff channel (/manager/staff-messages).
 * The layout's talk/keyboard input bar sends to whichever tab/thread is active
 * (via the 'pa-message' window event dispatched by ManagerLayout).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Users, MessageSquare, ChevronLeft, ChevronRight, Paperclip, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/ma-client';
import { useActiveClient } from './active-client';
import { useMessagesHeader } from './messages-header';
import { FormattedChatMessage } from '@/components/chat/ChatRenderer';

type Tab = 'clients' | 'staff';

function timeLabel(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay
    ? d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
}

const clean = (s: any) => String(s ?? '').replace(/[—–―‒]/g, '-');

export default function MessagesPage() {
  const { activeClient } = useActiveClient();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('clients');
  const [selectedClient, setSelectedClient] = useState<{ clientId: string; displayName: string } | null>(null);
  const [staffTarget, setStaffTarget] = useState<'everyone' | { userId: string; name: string } | null>(null);
  const [hint, setHint] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  // Keep each attachment's signed URL stable across the 3s poll (the backend
  // re-signs every fetch) so audio playback isn't restarted mid-play. Refresh
  // after 8 min, before the 10-min signed URL expires.
  const urlCacheRef = useRef<Map<string, { url: string; t: number }>>(new Map());
  const stableUrl = (id: string, url?: string | null): string | undefined => {
    if (!url) return undefined;
    const cached = urlCacheRef.current.get(id);
    const now = Date.now();
    if (cached && now - cached.t < 8 * 60 * 1000) return cached.url;
    urlCacheRef.current.set(id, { url, t: now });
    return url;
  };

  // The open client thread = the active client (header) or one picked from the list.
  const threadClient = activeClient ? { clientId: activeClient.id, displayName: activeClient.name } : selectedClient;

  // Tell the app header who's being chatted with (shown at the very top).
  const { setHeader } = useMessagesHeader();
  useEffect(() => {
    if (tab === 'clients') setHeader(threadClient ? threadClient.displayName : 'Messages', threadClient ? 'Client' : 'Clients');
    else setHeader(staffTarget ? (staffTarget === 'everyone' ? 'Everyone' : staffTarget.name) : 'Messages', 'Staff');
  }, [tab, threadClient?.clientId, threadClient?.displayName, staffTarget, setHeader]);
  useEffect(() => () => setHeader('', ''), [setHeader]);

  // ---- data ----
  const conversations = useQuery({
    enabled: tab === 'clients' && !threadClient,
    queryKey: ['ma-conversations'],
    queryFn: async () => { const r = await apiClient.get('/firm/client-messages/conversations'); return r.data; },
  });

  const thread = useQuery({
    enabled: tab === 'clients' && !!threadClient,
    queryKey: ['ma-thread', threadClient?.clientId],
    queryFn: async () => { const r = await apiClient.get(`/firm/client-messages/${threadClient!.clientId}`); return r.data; },
    refetchInterval: 3000,
  });

  // Staff: list of people (Everyone + each staff member), then a 1:1 / broadcast thread.
  const staffList = useQuery({
    enabled: tab === 'staff' && !staffTarget,
    queryKey: ['ma-staff-list'],
    queryFn: async () => { const r = await apiClient.get('/manager/staff-activity'); return r.data.data; },
  });
  const staffThread = useQuery({
    enabled: tab === 'staff' && !!staffTarget,
    queryKey: ['ma-staff-thread', staffTarget === 'everyone' ? 'everyone' : staffTarget?.userId],
    queryFn: async () => {
      const url = staffTarget === 'everyone' ? '/manager/staff-messages?limit=150' : `/manager/staff-messages?userId=${staffTarget!.userId}&limit=150`;
      const r = await apiClient.get(url);
      return r.data.data;
    },
    refetchInterval: 3000,
  });

  // Mark a client thread read when opened.
  useEffect(() => {
    if (tab === 'clients' && threadClient) {
      apiClient.put(`/firm/client-messages/${threadClient.clientId}/read`, {}).catch(() => {});
    }
  }, [tab, threadClient?.clientId]);

  // Auto-scroll to newest.
  const threadMsgs: any[] = thread.data?.messages || [];
  const staffMsgs: any[] = staffThread.data?.messages || [];
  const staffMe: string | undefined = staffThread.data?.me;
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [threadMsgs.length, staffMsgs.length, tab, threadClient?.clientId, staffTarget === 'everyone' ? 'everyone' : staffTarget?.userId]);

  // ---- unified send for the current target (client thread, or staff channel/DM) ----
  const targetReady = tab === 'clients' ? !!threadClient : tab === 'staff' ? !!staffTarget : false;
  const targetHint = tab === 'staff' ? 'Pick Everyone or a person first.' : 'Open a client chat first.';

  const presignUpload = async (filename: string, contentType: string, size: number) => {
    if (tab === 'staff') {
      const r = await apiClient.post('/manager/staff-messages', { action: 'upload-url', filename, contentType, fileSizeBytes: size });
      return r.data.data; // /manager/* responses are wrapped
    }
    const r = await apiClient.post(`/firm/client-messages/${threadClient!.clientId}/upload-url`, { filename, contentType, fileSizeBytes: size });
    return r.data; // /firm/client-messages/* responses are NOT wrapped
  };

  const postMessage = async (text: string, attachment?: any) => {
    if (tab === 'staff') {
      const recip = staffTarget === 'everyone' ? {} : { recipientUserId: (staffTarget as any).userId };
      await apiClient.post('/manager/staff-messages', { body: text, ...recip, attachment });
      await qc.invalidateQueries({ queryKey: ['ma-staff-thread'] });
    } else {
      const id = threadClient!.clientId;
      await apiClient.post(`/firm/client-messages/${id}`, { body: text, attachment });
      await qc.invalidateQueries({ queryKey: ['ma-thread', id] });
    }
  };

  const send = async (text: string) => {
    const body = text.trim();
    if (!body) return;
    if (!targetReady) { setHint(targetHint); return; }
    setHint('');
    try { await postMessage(body); } catch { setHint('Could not send. Try again.'); }
  };
  const sendRef = useRef(send);
  sendRef.current = send;
  useEffect(() => {
    const h = (e: Event) => { const t = (e as CustomEvent).detail; if (t) sendRef.current(t); };
    window.addEventListener('pa-message', h);
    return () => window.removeEventListener('pa-message', h);
  }, []);

  // ---- attachments + voice notes: presign -> S3 PUT -> send (clients AND staff) ----
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const uploadAndSend = async (data: Blob, filename: string, contentType: string) => {
    if (!targetReady) { setHint(targetHint); return; }
    if (data.size > 25 * 1024 * 1024) { setHint('File is too large (max 25 MB).'); return; }
    setHint('Sending...'); setUploading(true);
    try {
      const { uploadUrl, s3Key } = await presignUpload(filename, contentType, data.size);
      const put = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': contentType }, body: data });
      if (!put.ok) throw new Error('upload failed');
      await postMessage('', { s3Key, filename, contentType, fileSizeBytes: data.size });
      setHint('');
    } catch {
      setHint('Could not send that. Check the type and try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleAttach = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = '';
    if (file) uploadAndSend(file, file.name, file.type || 'application/octet-stream');
  };

  const sendVoiceNote = (blob: Blob, mime: string) => {
    const contentType = (mime || blob.type || 'audio/webm').split(';')[0];
    const ext = contentType.includes('mp4') ? 'm4a' : contentType.includes('ogg') ? 'ogg' : contentType.includes('mpeg') ? 'mp3' : contentType.includes('wav') ? 'wav' : 'webm';
    const filename = `voice-note-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.${ext}`;
    uploadAndSend(blob, filename, contentType);
  };
  const voiceRef = useRef(sendVoiceNote);
  voiceRef.current = sendVoiceNote;
  useEffect(() => {
    const onNote = (e: Event) => { const d = (e as CustomEvent).detail; if (d?.blob) voiceRef.current(d.blob, d.mime); };
    const onErr = (e: Event) => setHint(String((e as CustomEvent).detail || 'Microphone error.'));
    window.addEventListener('pa-voice-note', onNote);
    window.addEventListener('pa-voice-error', onErr);
    return () => { window.removeEventListener('pa-voice-note', onNote); window.removeEventListener('pa-voice-error', onErr); };
  }, []);

  const convos = useMemo(() => conversations.data?.conversations || [], [conversations.data]);

  return (
    <div className="flex h-full flex-col">
      {/* Shared hidden file input (used by both client + staff attach buttons) */}
      <input ref={fileRef} type="file" className="hidden" onChange={handleAttach}
        accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt" />

      {/* Tabs */}
      <div className="shrink-0 px-4 pt-3">
        <div className="flex gap-1 rounded-xl bg-muted/50 p-1">
          {(['clients', 'staff'] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => { setTab(t); setHint(''); }}
              className={cn(
                'flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2 text-sm font-medium transition',
                tab === t ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {t === 'clients' ? <MessageSquare className="h-4 w-4" /> : <Users className="h-4 w-4" />}
              {t === 'clients' ? 'Clients' : 'Staff'}
            </button>
          ))}
        </div>
      </div>

      {/* CLIENTS: conversation list (no thread open) */}
      {tab === 'clients' && !threadClient && (
        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3">
          {conversations.isLoading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[var(--v4-accent)]" /></div>
          ) : convos.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">No client conversations yet.</p>
          ) : (
            <div className="space-y-1.5">
              {convos.map((c: any) => (
                <button
                  key={c.clientId}
                  onClick={() => setSelectedClient({ clientId: c.clientId, displayName: c.displayName })}
                  className="flex w-full items-center gap-3 rounded-xl border border-border bg-card p-3 text-left transition hover:bg-muted/50"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--v4-accent)]/15 text-sm font-semibold text-[var(--v4-accent)]">
                    {(c.displayName?.[0] || '?').toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="truncate text-sm font-medium text-foreground">{c.displayName}</p>
                      <span className="shrink-0 text-[11px] text-muted-foreground">{timeLabel(c.lastAt)}</span>
                    </div>
                    <p className="truncate text-xs text-muted-foreground">{c.lastDirection === 'inbound' ? '' : 'You: '}{clean(c.lastMessage) || 'No messages'}</p>
                  </div>
                  {c.firmUnread > 0 && <span className="shrink-0 rounded-full bg-[var(--v4-accent)] px-1.5 py-0.5 text-[10px] font-semibold text-white">{c.firmUnread}</span>}
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* CLIENTS: open thread */}
      {tab === 'clients' && threadClient && (
        <>
          <div className="shrink-0 flex items-center gap-2 px-4 py-2">
            {!activeClient && (
              <button onClick={() => setSelectedClient(null)} className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-muted" aria-label="Back">
                <ChevronLeft className="h-4 w-4" />
              </button>
            )}
            <div className="flex-1" />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
              aria-label="Attach a file"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
            </button>
          </div>
          <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-2 space-y-2">
            {thread.isLoading ? (
              <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[var(--v4-accent)]" /></div>
            ) : threadMsgs.length === 0 ? (
              <p className="py-12 text-center text-sm text-muted-foreground">No messages with {threadClient.displayName} yet. Say hello below.</p>
            ) : (
              threadMsgs.map((m: any) => <Bubble key={m.id} mine={m.direction === 'outbound'} body={m.body} who={m.direction === 'outbound' ? m.senderName : threadClient.displayName} at={m.createdAt} attUrl={stableUrl(m.id, m.attachmentUrl)} attName={m.attachmentName} attType={m.attachmentContentType} />)
            )}
          </div>
        </>
      )}

      {/* STAFF: people list (Everyone + each staff member) */}
      {tab === 'staff' && !staffTarget && (
        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3">
          {staffList.isLoading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[var(--v4-accent)]" /></div>
          ) : (
            <div className="space-y-1.5">
              <button
                onClick={() => setStaffTarget('everyone')}
                className="flex w-full items-center gap-3 rounded-xl border border-border bg-card p-3 text-left transition hover:bg-muted/50"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--v4-accent)]/15 text-[var(--v4-accent)]"><Users className="h-5 w-5" /></div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground">Everyone</p>
                  <p className="text-xs text-muted-foreground">Message the whole team</p>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              </button>
              {(staffList.data?.staff || [])
                .filter((s: any) => s.user_id && s.user_id !== staffList.data?.me)
                .map((s: any) => {
                  const name = `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.email || 'Staff';
                  return (
                    <button
                      key={s.user_id}
                      onClick={() => setStaffTarget({ userId: s.user_id, name })}
                      className="flex w-full items-center gap-3 rounded-xl border border-border bg-card p-3 text-left transition hover:bg-muted/50"
                    >
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold text-foreground">
                        {(name.split(' ').map((p: string) => p[0]).join('').slice(0, 2) || '?').toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-foreground">{name}</p>
                        <p className="truncate text-xs text-muted-foreground">{s.email}</p>
                      </div>
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                    </button>
                  );
                })}
            </div>
          )}
        </div>
      )}

      {/* STAFF: open thread (Everyone or a 1:1 DM) */}
      {tab === 'staff' && staffTarget && (
        <>
          <div className="shrink-0 flex items-center gap-2 px-4 py-2">
            <button onClick={() => setStaffTarget(null)} className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-muted" aria-label="Back">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="flex-1" />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
              aria-label="Attach a file"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
            </button>
          </div>
          <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-2 space-y-2">
            {staffThread.isLoading ? (
              <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[var(--v4-accent)]" /></div>
            ) : staffMsgs.length === 0 ? (
              <p className="py-12 text-center text-sm text-muted-foreground">
                {staffTarget === 'everyone' ? 'No team messages yet. Start the conversation below.' : `No messages with ${staffTarget.name} yet. Say hello below.`}
              </p>
            ) : (
              staffMsgs.map((m: any) => <Bubble key={m.id} mine={!!staffMe && m.sender_user_id === staffMe} body={m.body} who={m.sender_name || 'Staff'} at={m.created_at} attUrl={stableUrl(m.id, m.attachmentUrl)} attName={m.attachment_name} attType={m.attachment_type} />)
            )}
          </div>
        </>
      )}

      {hint && <p className="shrink-0 px-4 pb-1 text-center text-xs text-amber-600 dark:text-amber-400">{hint}</p>}
    </div>
  );
}

function Bubble({ mine, body, who, at, showWho, attUrl, attName, attType }: {
  mine: boolean; body: string; who?: string; at?: string; showWho?: boolean;
  attUrl?: string | null; attName?: string | null; attType?: string | null;
}) {
  const isImage = !!attUrl && (attType || '').startsWith('image/');
  const isAudio = !!attUrl && (attType || '').startsWith('audio/');
  const hasText = !!String(body || '').trim();
  return (
    <div className={cn('flex flex-col', mine ? 'items-end' : 'items-start')}>
      <div className={cn(
        'max-w-[80%] overflow-hidden rounded-2xl text-sm',
        mine ? 'bg-[var(--v4-accent)] text-white rounded-br-sm' : 'bg-muted text-foreground rounded-bl-sm',
        (hasText || !attUrl || isAudio) && 'px-3 py-2'
      )}>
        {attUrl && isImage && (
          <a href={attUrl} target="_blank" rel="noopener" className={cn('block', hasText && '-mx-3 -mt-2 mb-2')}>
            <img src={attUrl} alt={attName || 'image'} className="max-h-60 w-full object-cover" />
          </a>
        )}
        {attUrl && isAudio && (
          <audio
            controls
            src={attUrl}
            preload="none"
            onPlay={(e) => { document.querySelectorAll('audio').forEach((a) => { if (a !== e.currentTarget) a.pause(); }); }}
            className={cn('w-56 max-w-full', hasText && 'mb-1.5')}
          />
        )}
        {attUrl && !isImage && !isAudio && (
          <a href={attUrl} target="_blank" rel="noopener"
            className={cn('flex items-center gap-2 rounded-lg px-2 py-1.5', mine ? 'bg-white/15' : 'bg-background/60', hasText && 'mb-1.5')}>
            <FileText className="h-4 w-4 shrink-0" />
            <span className="truncate text-xs underline">{attName || 'Attachment'}</span>
          </a>
        )}
        {hasText && (
          <div className="break-words [&_table]:my-1 [&_p]:my-0.5">
            <FormattedChatMessage content={body} darkTheme={mine} />
          </div>
        )}
      </div>
      <div className={cn('mt-0.5 flex flex-col px-1 text-[10px] leading-tight text-muted-foreground', mine ? 'items-end' : 'items-start')}>
        <span>{timeLabel(at)}</span>
        {who && <span className="font-medium text-foreground/70">{who}</span>}
      </div>
    </div>
  );
}
